import React, { useState, useEffect } from 'react';
import { Axios, Main, Loading } from '../../../Helpers/importacionesComunes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faWindowClose } from '@fortawesome/free-solid-svg-icons';
import { Spinner, Typography, Tabs, TabsHeader, Tab, TabsBody, TabPanel } from '@material-tailwind/react';
import { CurrencyDollarIcon, DocumentCheckIcon, PresentationChartBarIcon } from '@heroicons/react/24/solid';

// Convierte la fecha del ingreso (DD/MM/YYYY o ISO) a timestamp para poder ordenar.
const parseFechaIngreso = (f) => {
    if (!f) return 0;
    if (typeof f === 'string' && f.includes('/')) {
        const [d, m, y] = f.split('/');
        return new Date(`${y}-${m}-${d}T00:00:00`).getTime();
    }
    return new Date(f).getTime();
};

export function DashboardFinanciero({ id_iglesia, finalizar }) {
    const [ingresos, setIngresos] = useState([]);
    const [diezmos, setDiezmos] = useState([]);
    const [balances, setBalances] = useState([]);
    const [promedio, setPromedio] = useState(0);
    const [total, setTotal] = useState(0);
    const [buscando, setBuscando] = useState(false);
    const [huboError, setHuboError] = useState(false);

    const [activeTab, setActiveTab] = useState("ingresos");

    useEffect(() => {
        let mounted = true;
        setBuscando(true);

        const buscarDatos = async () => {
            try {
                // Hacemos las llamadas por separado y capturamos errores individuales para evitar que un 404 rompa todo
                const resIngresosP = Axios.get(`/api/tablasgenerales/ingresosigl/${id_iglesia || 0}`).catch(err => ({ data: [] }));
                const resDiezmosP = Axios.get(`/api/tablasgenerales/mesesdiezmados/${id_iglesia || 0}`).catch(err => ({ data: [] }));
                const resBalancesP = Axios.get(`/api/tablasgenerales/balances/${id_iglesia || 0}`).catch(err => ({ data: [] }));

                const [resIngresos, resDiezmos, resBalances] = await Promise.all([
                    resIngresosP,
                    resDiezmosP,
                    resBalancesP
                ]);

                console.log(resBalances.data);
                if (mounted) {
                    setIngresos(Array.isArray(resIngresos.data) ? resIngresos.data : []);
                    setDiezmos(Array.isArray(resDiezmos.data) ? resDiezmos.data : []);
                    setBalances(Array.isArray(resBalances.data) ? resBalances.data : []);
                    setBuscando(false);
                }
            } catch (err) {
                console.error('Error fetched dashboard:', err);
                if (mounted) {
                    setHuboError(true);
                    setBuscando(false);
                }
            }
        };

        buscarDatos();

        return () => { mounted = false; };
    }, [id_iglesia]);

    useEffect(() => {
        if (ingresos.length > 0 && diezmos.length > 0) {
            const registros_sin_null = ingresos.filter(item => item.monto != null && item.comprobante != ">>>>");
            const nueva_data = registros_sin_null.map(item => {
                return { ...item, monto: parseFloat(String(item.monto).replace(/,/g, "")) };
            });

            const suma = nueva_data.reduce((ac, item) => ac + item.monto, 0);
            setTotal(suma);

            const meses_diezmados = diezmos.filter(item => Number(item.diezmo) > 0).length;
            const prom = meses_diezmados > 0 ? suma / meses_diezmados : suma / 1;
            setPromedio(prom);
        } else {
            setTotal(0);
            setPromedio(0);
        }
    }, [ingresos, diezmos]);

    if (huboError) {
        return (
            <div className="flex flex-col justify-center items-center h-48 bg-white border border-red-200 mt-2 rounded">
                <span className="text-red-500 font-medium">Se produjo un error al cargar el panel financiero.</span>
            </div>
        );
    }

    if (buscando) {
        return (
            <div className='flex flex-col justify-center items-center h-64 bg-white mt-2 rounded shadow-sm border border-gray-100'>
                <Spinner className="h-10 w-10 text-blue-500" />
                <span className="mt-4 font-semibold text-gray-500 text-sm">Cargando datos del dashboard...</span>
            </div>
        );
    }

    // Ingresos ordenados del más reciente al más antiguo
    const ingresosOrdenados = [...ingresos].sort((a, b) => parseFechaIngreso(b.fecha) - parseFechaIngreso(a.fecha));

    const dataTabs = [
        {
            label: "Ingresos",
            value: "ingresos",
            icon: CurrencyDollarIcon,
            content: (
                <div className="flex flex-col h-full items-center p-4">
                    <div className="flex flex-col md:flex-row justify-center gap-6 mb-4 w-full">
                        <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100 flex-1 text-center">
                            <span className="block text-sm text-gray-500 uppercase tracking-wide">Total Último Año</span>
                            <span className="block text-2xl font-bold text-blue-600">${total.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}</span>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg shadow-sm border border-green-100 flex-1 text-center">
                            <span className="block text-sm text-gray-500 uppercase tracking-wide">Promedio Mensual</span>
                            <span className="block text-2xl font-bold text-green-600">${promedio.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}</span>
                        </div>
                    </div>
                    {ingresos.length === 0 ? (
                        <p className="text-gray-400 mt-6">- No se encontraron ingresos -</p>
                    ) : (
                        <div className="w-full max-w-4xl max-h-64 overflow-y-auto mt-2">
                             <table className="table-auto w-full text-left bg-white border border-gray-100 shadow-sm rounded">
                                <thead className="bg-gray-50 text-gray-600 text-sm">
                                    <tr>
                                        <th className="p-3 border-b font-medium">Fecha</th>
                                        <th className="p-3 border-b font-medium">Comprobante</th>
                                        <th className="p-3 border-b font-medium">Detalle</th>
                                        <th className="p-3 border-b font-medium text-right">Monto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ingresosOrdenados.map((item, i) => (
                                        <tr key={i} className="border-b hover:bg-gray-50 transition-colors text-sm">
                                            <td className="p-3 text-gray-700">{item.fecha}</td>
                                            <td className="p-3 text-gray-700 font-medium">{item.comprobante}</td>
                                            <td className="p-3 text-gray-600">{item.det_rc}</td>
                                            <td className="p-3 text-gray-800 font-bold text-right">{item.monto}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )
        },
        {
            label: "Balances",
            value: "balances",
            icon: DocumentCheckIcon,
            content: (
                <div className="flex flex-col items-center p-4">
                    {balances.length === 0 ? (
                        <p className="text-gray-400 mt-6">- No se encontraron balances -</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-4 w-full">
                             {balances.map((item, idx) => (
                                 <div key={idx} className={`p-3 rounded-lg text-center font-medium shadow-sm border ${item.estado == 0 ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                                     {item.periodo}
                                 </div>
                             ))}
                        </div>
                    )}
                </div>
            )
        },
        {
            label: "Diezmos",
            value: "diezmos",
            icon: PresentationChartBarIcon,
            content: (
                <div className="flex flex-col items-center p-4">
                     {diezmos.length === 0 ? (
                        <p className="text-gray-400 mt-6">- No se encontraron meses diezmados -</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-4 w-full">
                             {diezmos.map((item, idx) => (
                                 <div key={idx} className={`p-3 rounded-lg text-center font-medium shadow-sm border ${Number(item.diezmo) == 0 ? 'bg-red-50 text-red-600 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                                     {item.periodo}
                                 </div>
                             ))}
                        </div>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 m-2 relative custom-dash-fade-in w-[98%] max-w-5xl mx-auto">
            <button
                onClick={finalizar}
                className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors z-10"
                title="Cerrar Dashboard"
            >
                <FontAwesomeIcon icon={faWindowClose} size="lg" />
            </button>
            
            <div className="text-center mb-6">
                <Typography variant="h5" color="blue-gray" className="font-bold">
                    Dashboard Financiero
                </Typography>
                <Typography variant="small" color="gray" className="font-normal mt-1">
                    Resumen de ingresos, balances y diezmos
                </Typography>
            </div>

            <Tabs value={activeTab} className="bg-transparent">
                <TabsHeader
                    className="bg-gray-100/50 rounded-lg p-1 mx-auto max-w-2xl"
                    indicatorProps={{
                        className: "bg-white rounded-md shadow-sm border border-gray-200",
                    }}
                >
                    {dataTabs.map(({ label, value, icon }) => (
                        <Tab
                            key={value}
                            value={value}
                            onClick={() => setActiveTab(value)}
                            className="font-semibold text-gray-700 py-2 h-full z-20"
                        >   
                            <div className="flex items-center gap-2">
                                {React.createElement(icon, { className: "w-5 h-5" })}
                                {label}
                            </div>
                        </Tab>
                    ))}
                </TabsHeader>
                <TabsBody className="mt-4">
                    {dataTabs.map(({ value, content }) => (
                        <TabPanel key={value} value={value} className="p-0">
                            {content}
                        </TabPanel>
                    ))}
                </TabsBody>
            </Tabs>
        </div>
    );
}